import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Send,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../store/system.store';
import { AgentService, CreateAgentRequest } from '../../services/agent.service';

interface CreateAgentData {
  email: string;
  password?: string;
}

const CreateAgentPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState<CreateAgentData>({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [includePassword, setIncludePassword] = useState(false);
  const [errors, setErrors] = useState<Partial<CreateAgentData>>({});
  const [success, setSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Partial<CreateAgentData> = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation (only if include password is checked)
    if (includePassword && formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSuccess(false);

    try {
      const payload: CreateAgentRequest = {
        email: formData.email,
      };

      if (includePassword && formData.password) {
        payload.password = formData.password;
      }

      const response = await AgentService.createAgent(payload);

      if (response.success) {
        setSuccess(true);
        setFormData({ email: '', password: '' });
        setIncludePassword(false);
        
        dispatch(
          showSnackbar({
            isError: false,
            msg: response.message || 'Agent created successfully!',
          })
        );
      } else {
        throw new Error(response.message || 'Failed to create agent');
      }
    } catch (error) {
      console.error('Error creating agent:', error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: error instanceof Error ? error.message : 'Failed to create agent. Please try again.',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateAgentData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };


  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Box 
        sx={{ 
          padding: { xs: "16px", md: "24px" }, 
          backgroundColor: theme.palette.grey[50], 
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton 
            onClick={() => navigate('/admin')} 
            sx={{ 
              p: 1,
              bgcolor: 'white',
              boxShadow: 1,
              '&:hover': { bgcolor: theme.palette.grey[100] }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
            Create New Agent
          </Typography>
        </Box>

        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', maxWidth: 600 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              Agent Registration
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Agent created successfully! The user will receive login instructions via email.
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={includePassword}
                      onChange={(e) => {
                        setIncludePassword(e.target.checked);
                        if (!e.target.checked) {
                          setFormData(prev => ({ ...prev, password: '' }));
                          setErrors(prev => ({ ...prev, password: undefined }));
                        }
                      }}
                      color="primary"
                    />
                  }
                  label="Include Password"
                />

                {includePassword && (
                  <TextField
                    fullWidth
                    label="Password (Optional)"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    error={!!errors.password}
                    helperText={errors.password || 'Minimum 8 characters'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                <Alert severity="info">
                  If no password is provided, the new agent will receive an email 
                  with instructions to set up their password.
                </Alert>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading || !formData.email}
                  startIcon={<Send />}
                  sx={{ textTransform: 'none' }}
                >
                  {isLoading ? 'Creating Agent...' : 'Create Agent'}
                </Button>
              </Stack>
            </form>

          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CreateAgentPage;